import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Avatar as UiAvatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { StaffBadge } from '@/components/ui/StaffBadge';
import { UsernameBadge } from '@/components/ui/UsernameBadge';
import type { ReportReason, ReportTarget } from '@/features/reports/ReportSheet';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { type CommentView, type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import { formatRelativeTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getProfileHref } from '@/lib/navigation/routes';
import { router } from 'expo-router';
import { Building2Icon, CheckIcon, MoreHorizontalIcon, SendHorizonalIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Modal, Pressable, ScrollView, TextInput, View } from 'react-native';

function Avatar({
  name,
  avatarURL,
  size,
  accent = false,
  borderColor,
}: {
  name: string;
  avatarURL?: string;
  size: number;
  accent?: boolean;
  borderColor?: string;
}) {
  const initials = getProfileInitials(name);

  return (
    <UiAvatar
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: accent || borderColor ? 2 : 0,
        borderColor: accent ? DESIGN_TOKENS.colors.accentStrong : borderColor,
        backgroundColor: accent ? DESIGN_TOKENS.colors.accent : DESIGN_TOKENS.colors.canvasInset,
      }}>
      {avatarURL ? (
        <AvatarImage source={{ uri: avatarURL }} style={{ width: size, height: size }} />
      ) : null}
      <AvatarFallback className="bg-transparent">
        <Text
          variant="small"
          className={
            accent ? 'font-semibold text-primary-foreground' : 'font-semibold text-foreground'
          }>
          {initials}
        </Text>
      </AvatarFallback>
    </UiAvatar>
  );
}

export function CommentRow({
  comment,
  canDelete,
  isAuthenticated,
  onDelete,
  onHide,
  onUnhide,
  onReport,
  onOpenProfile,
}: {
  comment: CommentView;
  canDelete: boolean;
  isAuthenticated: boolean;
  onDelete: () => void;
  onHide: () => void;
  onUnhide: () => void;
  onReport: () => void;
  onOpenProfile: () => void;
}) {
  const [isActionsVisible, setIsActionsVisible] = React.useState(false);

  function closeActions() {
    setIsActionsVisible(false);
  }

  function runAction(action: () => void) {
    closeActions();
    requestAnimationFrame(() => {
      action();
    });
  }

  if (comment.is_hidden && comment.hidden_by_me) {
    return (
      <Pressable
        className="flex-row items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
        onPress={onUnhide}>
        <Text variant="small" className="flex-1 italic text-muted-foreground">
          Hidden comment.
        </Text>
        <Text variant="small" className="text-muted-foreground">
          Tap to show
        </Text>
      </Pressable>
    );
  }

  const showActions = isAuthenticated && (canDelete || !comment.is_hidden);

  return (
    <View className="flex-row items-start gap-3">
      <Pressable onPress={onOpenProfile}>
        <Avatar
          name={comment.author.display_name}
          avatarURL={comment.author.avatar_url}
          size={40}
        />
      </Pressable>

      <View className="flex-1">
        <View className="flex-row items-start gap-3">
          <View className="flex-1">
            <Pressable onPress={onOpenProfile} className="flex-row items-center gap-1.5">
              {comment.author.is_venue ? (
                <>
                  <Icon as={Building2Icon} size={13} className="text-primary" />
                  <Text variant="small" className="font-semibold text-foreground">
                    {comment.author.venue_name || comment.author.display_name}
                  </Text>
                  {comment.author.is_staff ? <StaffBadge className="ml-1" /> : null}
                </>
              ) : (
                <UsernameBadge
                  username={comment.author.username}
                  isStaff={comment.author.is_staff}
                  prefix=""
                  textClassName="font-semibold text-foreground"
                />
              )}
              <Text variant="small" className="text-muted-foreground">
                ·
              </Text>
              <Text variant="small" className="text-muted-foreground">
                {formatRelativeTime(comment.created_at)}
              </Text>
            </Pressable>
          </View>

          {showActions ? (
            <Button
              variant="ghost"
              size="icon"
              className="-mr-2 -mt-1 size-8 rounded-full"
              onPress={() => setIsActionsVisible(true)}
              accessibilityLabel="Open comment actions">
              <Icon as={MoreHorizontalIcon} size={16} className="text-muted-foreground" />
            </Button>
          ) : null}
        </View>

        <Text variant="small" className="mt-1 leading-6 text-foreground/80">
          {comment.body}
        </Text>
      </View>

      <Modal
        visible={isActionsVisible}
        transparent
        animationType="fade"
        onRequestClose={closeActions}>
        <View className="flex-1 justify-center bg-black/40 px-6">
          <Pressable className="absolute inset-0" onPress={closeActions} />
          <View className="rounded-[24px] border border-border-strong bg-surface-overlay p-4">
            <View className="mb-2 flex-row items-center justify-between gap-3">
              <Text
                variant="small"
                className="font-semibold uppercase tracking-[1.6px] text-muted-foreground">
                {canDelete ? 'Manage Comment' : 'Comment Actions'}
              </Text>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 rounded-full"
                onPress={closeActions}
                accessibilityLabel="Close comment actions">
                <Icon as={XIcon} size={15} className="text-muted-foreground" />
              </Button>
            </View>

            <View className="gap-2">
              {canDelete ? (
                <Button
                  variant="destructive"
                  className="justify-start rounded-[16px] px-4"
                  onPress={() => runAction(onDelete)}>
                  <Text>Delete Comment</Text>
                </Button>
              ) : null}

              {!comment.is_hidden ? (
                <Button
                  variant="secondary"
                  className="justify-start rounded-[16px] px-4"
                  onPress={() => runAction(onHide)}>
                  <Text>Hide Comment</Text>
                </Button>
              ) : null}

              {!canDelete ? (
                <Button
                  variant="secondary"
                  className="justify-start rounded-[16px] px-4"
                  onPress={() => runAction(onReport)}>
                  <Text>Report Comment</Text>
                </Button>
              ) : null}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

export function CommentComposer({
  value,
  onChangeText,
  onFocus,
  onBlur,
  autoFocus = false,
  className,
  isSubmitting,
  profileName,
  avatarURL,
  onSubmit,
}: {
  value: string;
  onChangeText: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  autoFocus?: boolean;
  className?: string;
  isSubmitting: boolean;
  profileName: string;
  avatarURL?: string;
  onSubmit: () => void;
}) {
  const inputRef = React.useRef<TextInput | null>(null);
  React.useEffect(() => {
    if (!autoFocus) {
      return;
    }

    const timeout = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(timeout);
  }, [autoFocus]);

  return (
    <View className={className ?? 'mt-5'}>
      <Pressable
        accessibilityLabel="Add a comment"
        accessibilityRole="button"
        className="flex-row items-center gap-3 rounded-[22px] border border-border-strong bg-surface-raised p-3"
        onPress={() => inputRef.current?.focus()}>
        <Avatar name={profileName} avatarURL={avatarURL} size={36} />
        <TextInput
          ref={inputRef}
          value={value}
          onChangeText={onChangeText}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder="Add a comment"
          placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
          className="flex-1 text-[15px] text-foreground"
        />
        <Pressable
          accessibilityLabel="Submit comment"
          accessibilityRole="button"
          className="size-10 items-center justify-center rounded-full"
          style={{
            backgroundColor: value.trim()
              ? DESIGN_TOKENS.colors.accent
              : DESIGN_TOKENS.colors.canvasInset,
          }}
          disabled={isSubmitting || !value.trim()}
          onPress={onSubmit}>
          <Icon
            as={SendHorizonalIcon}
            size={16}
            className={value.trim() ? 'text-[#111318]' : 'text-muted-foreground'}
          />
        </Pressable>
      </Pressable>
    </View>
  );
}

export function CommentComposerLauncher({
  profileName,
  avatarURL,
  onPress,
}: {
  profileName: string;
  avatarURL?: string;
  onPress: () => void;
}) {
  return (
    <View className="mt-5">
      <Pressable
        accessibilityLabel="Add a comment"
        accessibilityRole="button"
        className="flex-row items-center gap-3 rounded-[22px] border border-border-strong bg-surface-raised p-3"
        onPress={onPress}>
        <Avatar name={profileName} avatarURL={avatarURL} size={36} />
        <Text className="flex-1 text-[15px] text-muted-foreground">Add a comment</Text>
        <View
          className="size-10 items-center justify-center rounded-full"
          style={{ backgroundColor: DESIGN_TOKENS.colors.canvasInset }}>
          <Icon as={SendHorizonalIcon} size={16} className="text-muted-foreground" />
        </View>
      </Pressable>
    </View>
  );
}

export function AttendeeModal({
  visible,
  attendees,
  totalCount,
  onClose,
}: {
  visible: boolean;
  attendees: RadiousEventDetail['attendee_preview'];
  totalCount: number;
  onClose: () => void;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View className="flex-1 justify-end px-4 pb-8">
        <Pressable className="absolute inset-0 bg-black/70" onPress={onClose} />
        <View className="rounded-[28px] border border-border-strong bg-surface-overlay p-4">
          <View className="flex-row items-center justify-between gap-3">
            <View>
              <Text variant="large" className="text-xl font-semibold">
                {totalCount} going
              </Text>
              <Text variant="small" className="mt-1 text-muted-foreground">
                People going to this event.
              </Text>
            </View>
            <Pressable
              className="rounded-full border border-border-strong bg-surface-raised px-3 py-2"
              onPress={onClose}>
              <Text variant="small" className="text-muted-foreground">
                Close
              </Text>
            </Pressable>
          </View>

          <ScrollView
            className="mt-5"
            style={{ maxHeight: 360 }}
            showsVerticalScrollIndicator={false}>
            <View className="gap-4 pb-1">
              {attendees.map((attendee) => (
                <Pressable
                  key={attendee.user_id}
                  className="flex-row items-center gap-3"
                  onPress={() => {
                    onClose();
                    router.push(
                      getProfileHref(attendee.username, {
                        displayName: attendee.display_name,
                        bio: '',
                      })
                    );
                  }}>
                  <Avatar
                    name={attendee.display_name}
                    avatarURL={attendee.avatar_url}
                    size={42}
                    accent={attendee.is_friend}
                  />
                  <View className="flex-1">
                    <Text variant="small" className="font-semibold text-foreground">
                      {attendee.display_name}
                    </Text>
                    <Text variant="small" className="mt-0.5 text-muted-foreground">
                      @{attendee.username}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const WEB_REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate' },
  { value: 'wrong_info', label: 'Wrong info' },
  { value: 'other', label: 'Other' },
];

export function WebReportModal({
  target,
  onReport,
  onClose,
}: {
  target: ReportTarget;
  onReport: (reason: ReportReason, details?: string) => Promise<void>;
  onClose: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedReason, setSelectedReason] = React.useState<ReportReason | null>(null);
  const [details, setDetails] = React.useState('');

  const title =
    target.type === 'event'
      ? `Report "${target.title}"`
      : target.type === 'user'
        ? `Report @${target.username}`
        : 'Report comment';

  async function handleSubmit() {
    if (isSubmitting || !selectedReason) return;
    setIsSubmitting(true);
    try {
      await onReport(selectedReason, details.trim() || undefined);
      setSelectedReason(null);
      setDetails('');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    setSelectedReason(null);
    setDetails('');
    onClose();
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={handleClose}>
      <View className="flex-1 items-center justify-center bg-black/70 px-4">
        <View className="w-full max-w-md rounded-[28px] border border-border-strong bg-surface-overlay p-5">
          <View className="mb-4 flex-row items-center justify-between">
            <Text variant="large" className="flex-1 text-xl font-semibold" numberOfLines={1}>
              {title}
            </Text>
            <Pressable
              onPress={handleClose}
              className="size-8 items-center justify-center rounded-full bg-surface-raised">
              <Icon as={XIcon} size={16} className="text-muted-foreground" />
            </Pressable>
          </View>

          <Text variant="small" className="mb-3 text-muted-foreground">
            Select a reason:
          </Text>

          <View className="mb-4 flex-row flex-wrap gap-2">
            {WEB_REPORT_REASONS.map((reason) => {
              const isSelected = selectedReason === reason.value;
              return (
                <Pressable
                  key={reason.value}
                  onPress={() => setSelectedReason(reason.value)}
                  className="flex-row items-center gap-2 rounded-full px-4 py-2"
                  style={{
                    backgroundColor: isSelected
                      ? DESIGN_TOKENS.colors.accent
                      : DESIGN_TOKENS.colors.surfaceRaised,
                    borderWidth: 1,
                    borderColor: isSelected
                      ? DESIGN_TOKENS.colors.accent
                      : DESIGN_TOKENS.colors.border,
                  }}>
                  {isSelected ? (
                    <Icon as={CheckIcon} size={14} color={DESIGN_TOKENS.colors.canvasAlt} />
                  ) : null}
                  <Text
                    variant="small"
                    style={{
                      color: isSelected ? DESIGN_TOKENS.colors.canvasAlt : DESIGN_TOKENS.colors.text,
                    }}>
                    {reason.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Text variant="small" className="mb-2 text-muted-foreground">
            Additional details (optional):
          </Text>

          <TextInput
            value={details}
            onChangeText={setDetails}
            placeholder="Tell us more about the issue..."
            placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
            multiline
            numberOfLines={3}
            className="min-h-20 rounded-xl bg-surface-raised p-3 text-sm text-foreground"
            style={{
              minHeight: 80,
            }}
          />

          <Pressable
            onPress={handleSubmit}
            disabled={isSubmitting || !selectedReason}
            className="mt-4 items-center justify-center rounded-xl py-3"
            style={{
              backgroundColor: selectedReason
                ? DESIGN_TOKENS.colors.accent
                : DESIGN_TOKENS.colors.surfaceRaised,
              opacity: isSubmitting ? 0.5 : 1,
            }}>
            <Text
              variant="default"
              className="font-semibold"
              style={{
                color: selectedReason ? DESIGN_TOKENS.colors.canvasAlt : DESIGN_TOKENS.colors.textMuted,
              }}>
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Text>
          </Pressable>

          <Text variant="small" className="mt-3 text-center text-muted-foreground">
            Your report is anonymous
          </Text>
        </View>
      </View>
    </Modal>
  );
}
